import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NativeSelect, TextInput } from "@mantine/core";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { RecordFlag } from "../api/types";
import { shortDate } from "../lib/format";
import { Button } from "../ui/Button";
import { Fact, FactActions, FactForm, FactSaid } from "../ui/Facts";
import { Spinner } from "./Spinner";

/* Say a record is wrong, without changing it.
 *
 * This is what the first release has instead of letting somebody merge two
 * records by hand. A merge is destructive, bad merges here are demonstrated
 * rather than hypothetical, and an easy merge button is how two unrelated firms
 * become one record. A flag captures the same judgment and costs nothing to be
 * wrong about.
 *
 * The reason is a short list rather than a free-text box, because a pile of
 * sentences cannot be sorted and each of these five would be acted on
 * differently. The sentence still has to be written, because "wrong" on its own
 * tells the next person nothing.
 *
 * On the Details tab this is one fact, "Flags", with the open flags as its
 * value and the form opening under it. */
const REASONS = [
  { key: "duplicate", label: "It is the same as another record" },
  { key: "wrong_details", label: "Some of the details are wrong" },
  { key: "not_relevant", label: "It does not belong in EVIE" },
  { key: "gone", label: "The business or property is gone" },
  { key: "other", label: "Something else" },
] as const;

export function FlagFacts({
  token, entityType, entityId, open: openAsked, onOpenChange,
}: {
  token: string; entityType: string; entityId: string;
  /** The page can open the form itself, from the header's action. */
  open?: boolean; onOpenChange?: (open: boolean) => void;
}) {
  const [own, setOwn] = useState(false);
  const open = openAsked ?? own;
  const setOpen = (v: boolean) => { setOwn(v); onOpenChange?.(v); };
  const [reason, setReason] = useState<string>("wrong_details");
  const [note, setNote] = useState("");
  const [said, setSaid] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const flags = useQuery({
    queryKey: ["flags", entityType, entityId],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("record_flag")
        .select("id,reason,note,flagged_by,flagged_at,resolved_at,resolution")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .order("flagged_at", { ascending: false, nullsFirst: false })
        .limit(20);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as RecordFlag[];
    },
  });

  const save = useMutation({
    mutationFn: () =>
      callFunction<string>(token, "flag_record", {
        p_entity_type: entityType,
        p_entity_id: entityId,
        p_reason: reason,
        p_note: note.trim(),
      }),
    onSuccess: () => {
      // Says what did not happen as well as what did, because a person
      // pressing this on a record they believe is wrong will reasonably expect
      // it to disappear.
      setSaid("Flagged with your name and date. The record stays in every list.");
      setNote("");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["flags", entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ["flags-open", entityType, entityId] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  const openFlags = (flags.data ?? []).filter((f) => !f.resolved_at);
  const canSave = note.trim().length >= 3 && !save.isPending;

  return (
    <>
      <Fact label="Flags" id="flag">
        {flags.isLoading ? <Spinner />
          : flags.isError ? <span className="refusal">{(flags.error as Error).message}</span>
          : !openFlags.length ? <span className="muted">none open</span>
          : (
            <ul className="fact-lines">
              {openFlags.map((f) => (
                <li key={f.id}>
                  <strong>{REASONS.find((r) => r.key === f.reason)?.label ?? f.reason}</strong>
                  {": "}{f.note}{" "}
                  <span className="muted">{f.flagged_by ?? "somebody"}, {shortDate(f.flagged_at)}</span>
                </li>
              ))}
            </ul>
          )}
      </Fact>
      {open ? (
        <FactForm label="Flag this record">
          <NativeSelect id="flag-reason" label="What is wrong" value={reason} autoFocus
                        onChange={(e) => { setReason(e.currentTarget.value); setSaid(null); }}
                        data={REASONS.map((r) => ({ value: r.key, label: r.label }))} />
          <TextInput id="flag-note" label="In your words" value={note} w="20rem"
                     onChange={(e) => { setNote(e.currentTarget.value); setSaid(null); }}
                     onKeyDown={(e) => { if (e.key === "Enter" && canSave) { e.preventDefault(); save.mutate(); } }} />
          <Button kind="orange" size="compact-sm" onClick={() => save.mutate()} disabled={!canSave}>
            {save.isPending ? <><Spinner />Saving</> : "Flag it"}
          </Button>
          <Button kind="link" size="compact-sm" onClick={() => { setOpen(false); setSaid(null); }}>Cancel</Button>
        </FactForm>
      ) : (
        <FactActions>
          <Button kind="link" size="compact-sm" onClick={() => { setOpen(true); setSaid(null); }}>
            Flag this as wrong
          </Button>
        </FactActions>
      )}
      {said ? <FactSaid error={save.isError}>{said}</FactSaid> : null}
    </>
  );
}
